import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
    CheckCircle2,
    Copy,
    ExternalLink,
    Loader2,
    AlertCircle,
    Users,
} from "lucide-react";
import { CHECKOUT_SUCCESS } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15; // ~30 s de espera máxima

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");

    const [state, setState] = useState({
        // "polling" | "paid" | "expired" | "error"
        phase: "polling",
        message: "Confirmando tu pago...",
        wedding: null,
    });

    // useRef para el contador de intentos — no queremos re-renders por esto.
    const attemptsRef = useRef(0);
    const cancelRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState({
                phase: "error",
                message: "Falta el identificador de sesión.",
                wedding: null,
            });
            return;
        }
        cancelRef.current = false;

        const poll = async () => {
            if (cancelRef.current) return;
            try {
                const res = await axios.get(
                    `${API}/checkout/status/${sessionId}`,
                );
                if (cancelRef.current) return;
                const { payment_status, status, wedding } = res.data;

                if (payment_status === "paid" && wedding) {
                    setState({
                        phase: "paid",
                        message: "Pago confirmado",
                        wedding,
                    });
                    return;
                }
                if (status === "expired") {
                    setState({
                        phase: "expired",
                        message: "La sesión de pago ha expirado.",
                        wedding: null,
                    });
                    return;
                }
                // sigue en pending — reintentar
                attemptsRef.current += 1;
                if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
                    setState({
                        phase: "error",
                        message:
                            "Estamos tardando más de lo esperado. Revisa tu email; te avisaremos.",
                        wedding: null,
                    });
                    return;
                }
                setTimeout(poll, POLL_INTERVAL_MS);
            } catch (err) {
                console.error("checkout status failed", err);
                if (cancelRef.current) return;
                setState({
                    phase: "error",
                    message:
                        "No hemos podido comprobar el estado del pago.",
                    wedding: null,
                });
            }
        };
        poll();

        return () => {
            cancelRef.current = true;
        };
    }, [sessionId]);

    if (state.phase === "polling") {
        return (
            <div
                data-testid={CHECKOUT_SUCCESS.polling}
                className="h-[100dvh] w-full flex flex-col items-center justify-center px-6 text-center bg-[var(--bg)]"
            >
                <Loader2
                    size={40}
                    className="animate-spin text-[var(--ink-soft)] mb-4"
                />
                <h1 className="font-serif text-3xl mb-2">
                    {state.message}
                </h1>
                <p className="text-sm text-[var(--ink-soft)] max-w-xs">
                    Estamos verificando el pago con Stripe. No cierres esta
                    pestaña.
                </p>
            </div>
        );
    }

    if (state.phase === "expired" || state.phase === "error") {
        return (
            <div
                data-testid={CHECKOUT_SUCCESS.error}
                className="h-[100dvh] w-full flex flex-col items-center justify-center px-6 text-center bg-[var(--bg)]"
            >
                <AlertCircle
                    size={40}
                    className="text-red-500 mb-4"
                />
                <h1 className="font-serif text-3xl mb-2">Algo salió mal</h1>
                <p className="text-sm text-[var(--ink-soft)] max-w-sm mb-6">
                    {state.message}
                </p>
                <Link to="/editor" className="btn-primary">
                    Volver al editor
                </Link>
            </div>
        );
    }

    // paid
    return <PaidView wedding={state.wedding} />;
}

function PaidView({ wedding }) {
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [copiedCode, setCopiedCode] = useState(false);

    const publicUrl =
        typeof window !== "undefined" && wedding.slug
            ? `${window.location.origin}/w/${wedding.slug}`
            : "";

    const copy = async (text, setter) => {
        try {
            await navigator.clipboard.writeText(text);
            setter(true);
            setTimeout(() => setter(false), 2000);
        } catch (err) {
            console.warn("copy failed", err);
        }
    };

    return (
        <div
            data-testid={CHECKOUT_SUCCESS.root}
            className="min-h-[100dvh] w-full flex items-start justify-center px-5 py-10 bg-[var(--bg)]"
        >
            <div className="w-full max-w-[520px]">
                <div className="w-14 h-14 mx-auto rounded-full bg-[var(--ink)] text-[#fbf9f6] flex items-center justify-center mb-4">
                    <CheckCircle2 size={22} />
                </div>
                <h1 className="font-serif text-3xl md:text-4xl text-center mb-2">
                    ¡Vuestra web está publicada!
                </h1>
                <p className="text-sm text-[var(--ink-soft)] text-center mb-8">
                    Gracias, {wedding.primary_name || "novios"}. Guarda estos
                    dos datos con mimo: son la llave de tu boda.
                </p>

                {/* URL pública */}
                <div className="card-soft p-5 mb-4">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--ink-soft)] mb-2">
                        Vuestra URL pública
                    </div>
                    <div className="flex items-center gap-2">
                        <code
                            data-testid={CHECKOUT_SUCCESS.publicUrl}
                            className="font-mono text-sm truncate text-left flex-1"
                        >
                            {publicUrl}
                        </code>
                        <button
                            data-testid={CHECKOUT_SUCCESS.copyUrl}
                            onClick={() => copy(publicUrl, setCopiedUrl)}
                            className="btn-ghost"
                            style={{ padding: "8px 14px", fontSize: 12 }}
                        >
                            <Copy size={13} />
                            {copiedUrl ? "Copiada" : "Copiar"}
                        </button>
                    </div>
                    <a
                        data-testid={CHECKOUT_SUCCESS.openUrl}
                        href={`/w/${wedding.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-primary w-full mt-4 justify-center"
                    >
                        <ExternalLink size={14} />
                        Abrir mi web
                    </a>
                </div>

                {/* Código de boda */}
                {wedding.wedding_code && (
                    <div className="card-soft p-5">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--ink-soft)] mb-2">
                            Vuestro código de administrador
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <code
                                data-testid={CHECKOUT_SUCCESS.weddingCode}
                                className="font-mono text-xl tracking-[0.3em] font-semibold"
                            >
                                {wedding.wedding_code}
                            </code>
                            <button
                                data-testid={CHECKOUT_SUCCESS.copyCode}
                                onClick={() =>
                                    copy(wedding.wedding_code, setCopiedCode)
                                }
                                className="btn-ghost"
                                style={{ padding: "8px 14px", fontSize: 12 }}
                            >
                                <Copy size={13} />
                                {copiedCode ? "Copiado" : "Copiar"}
                            </button>
                        </div>
                        <p className="text-[11px] text-[var(--ink-soft)] mt-3 leading-relaxed">
                            Con este código accederéis a{" "}
                            <strong>/admin</strong> para gestionar invitados,
                            RSVP, alergias y editar la web cuando queráis.
                        </p>
                        <Link
                            to="/admin"
                            data-testid={CHECKOUT_SUCCESS.goToAdmin}
                            className="btn-ghost w-full mt-4 justify-center"
                        >
                            <Users size={14} />
                            Ir al panel de invitados
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
